import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  TextField, 
  Typography, 
  Button, 
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

/**
 * A reusable component that provides inline editing functionality
 * for text values.
 */
const InlineEditableField = ({
  // Current value
  value,
  // Function called when value is saved (should return Promise for async validation)
  onSave,
  // Optional display/styling props
  displayStyles = {},
  placeholder = 'Click to edit',
  label,
  editButtonPosition = 'none', // 'none', 'left', 'right', or 'bottom'
  showEditButton = false,
  editButtonText = 'Edit',
  editButtonIcon = <EditIcon />,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  inputProps = {},
  fullWidth = true,
  variant = 'outlined',
  size = 'medium',
  autoFocus = true,
  multiline = false,
  editOnClick = true,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStartEdit = (e) => {
    if (disabled) return;
    
    e.stopPropagation();
    setEditingValue(value);
    setIsEditing(true);
    setError('');
  };

  const handleCancel = (e) => {
    if (e) e.stopPropagation();
    setIsEditing(false);
    setEditingValue('');
    setError('');
  };

  const handleChange = (e) => {
    setEditingValue(e.target.value);
    if (error) setError('');
  };

  const handleSave = async (e) => {
    if (e) e.stopPropagation();
    
    if (!editingValue.trim()) {
      setError('Value cannot be empty');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // onSave should return a Promise that resolves when save is successful
      // or rejects with an error message
      await onSave(editingValue);
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const EditButton = () => (
    <Button
      startIcon={editButtonIcon}
      onClick={handleStartEdit}
      size="small"
      variant="outlined"
      disabled={disabled}
      sx={{ 
        alignSelf: 'flex-start',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        }
      }}
    >
      {editButtonText}
    </Button>
  );

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {label && (
          <Typography variant="body2" component="label" fontWeight="medium">
            {label}
          </Typography>
        )}
        <TextField
          value={editingValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          error={Boolean(error)}
          helperText={error}
          variant={variant}
          size={size}
          autoFocus={autoFocus}
          multiline={multiline}
          fullWidth={fullWidth}
          disabled={isSubmitting}
          {...inputProps}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<CheckIcon />}
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={isSubmitting}
          >
            {confirmButtonText}
          </Button>
          <Button
            startIcon={<CloseIcon />}
            onClick={handleCancel}
            variant="outlined"
            size="small"
            disabled={isSubmitting}
          >
            {cancelButtonText}
          </Button>
        </Box>
      </Box>
    );
  }

  // Different layouts based on editButtonPosition
  const renderEditButtonBefore = editButtonPosition === 'left' && showEditButton;
  const renderEditButtonAfter = editButtonPosition === 'right' && showEditButton;
  const renderEditButtonBottom = editButtonPosition === 'bottom' && showEditButton;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <Typography variant="body2" component="label" fontWeight="medium" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        width: '100%',
        gap: 1,
      }}>
        {renderEditButtonBefore && <EditButton />}
        
        <Typography 
          variant="body1" 
          onClick={editOnClick ? handleStartEdit : undefined}
          sx={{ 
            padding: '8px 12px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            width: '100%',
            backgroundColor: disabled ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
            wordBreak: 'break-word',
            cursor: editOnClick && !disabled ? 'pointer' : 'default',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: editOnClick && !disabled ? 'rgba(0, 0, 0, 0.08)' : undefined,
              borderColor: editOnClick && !disabled ? 'rgba(0, 0, 0, 0.42)' : undefined,
            },
            ...displayStyles
          }}
          role={editOnClick && !disabled ? "button" : undefined}
          aria-label={editOnClick && !disabled ? "Click to edit" : undefined}
        >
          {value || placeholder}
        </Typography>
        
        {renderEditButtonAfter && <EditButton />}
      </Box>
      
      {renderEditButtonBottom && (
        <Box sx={{ mt: 1 }}>
          <EditButton />
        </Box>
      )}
    </Box>
  );
};

InlineEditableField.propTypes = {
  value: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  displayStyles: PropTypes.object,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  editButtonPosition: PropTypes.oneOf(['none', 'left', 'right', 'bottom']),
  showEditButton: PropTypes.bool,
  editButtonText: PropTypes.string,
  editButtonIcon: PropTypes.node,
  confirmButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  inputProps: PropTypes.object,
  fullWidth: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.string,
  autoFocus: PropTypes.bool,
  multiline: PropTypes.bool,
  editOnClick: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default InlineEditableField; 